import { getSupabaseClient, type DdmSupabaseClient } from '../supabase';

/**
 * Result row shape for `search_dld_zones` RPC. Mirrors the SQL RETURNS TABLE
 * in migration 20260522010100_dld_zones_search_rpc.sql.
 */
export type DldZone = {
  zone_key: string;
  display_name: string;
  transaction_count: number;
  source: string;
};

/**
 * Result row shape for `search_dld_communities` RPC (PR-3 signature). Carries
 * the `zone_key` alongside the display value so the front-end can auto-fill
 * the zone form field when a community is committed.
 */
export type DldCommunity = {
  community_key: string;
  display_name: string;
  zone: string | null;
  zone_key: string | null;
  transaction_count: number;
  source: string;
};

/**
 * Result row shape for `search_dld_buildings` RPC (PR-3 signature). Carries
 * the `community_key` AND `zone_key` so the front-end can auto-fill both
 * higher-level form fields when a building is committed.
 */
export type DldBuilding = {
  building_key: string;
  display_name: string;
  community_key: string | null;
  community_display_name: string | null;
  zone: string | null;
  zone_key: string | null;
  transaction_count: number;
  source: string;
};

/**
 * Autocomplete-friendly zone lookup (PR-3). Empty `query` returns the top N
 * zones ordered by transaction_count DESC. Non-empty query applies ILIKE +
 * pg_trgm similarity ordering. SECURITY INVOKER RPC, EXECUTE granted to
 * anon + authenticated.
 */
export async function searchDldZones(
  query: string,
  limit = 20,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<DldZone[]> {
  const { data, error } = await client.rpc('search_dld_zones', {
    p_query: query,
    p_limit: limit,
  });
  if (error) throw error;
  return (data ?? []) as DldZone[];
}

/**
 * Autocomplete-friendly community lookup. Optional `zoneKey` filter for the
 * 3-level cascading combobox (zone → community). The RPC also returns
 * `zone_key` so the caller can auto-fill the zone field upstream when a
 * community is committed.
 */
export async function searchDldCommunities(
  query: string,
  zoneKey: string | null = null,
  limit = 20,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<DldCommunity[]> {
  const { data, error } = await client.rpc('search_dld_communities', {
    p_query: query,
    // PostgREST generated types declare `p_zone_key?: string` — translate
    // `null` to `undefined` so the parameter is omitted and the RPC default
    // (NULL) kicks in.
    p_zone_key: zoneKey ?? undefined,
    p_limit: limit,
  });
  if (error) throw error;
  return (data ?? []) as DldCommunity[];
}

/**
 * Autocomplete-friendly building lookup. Optional `communityKey` + `zoneKey`
 * filters for the 3-level cascading combobox. The RPC also returns
 * `community_key` AND `zone_key` so the caller can auto-fill both upstream
 * form fields when a building is committed.
 */
export async function searchDldBuildings(
  query: string,
  communityKey: string | null = null,
  zoneKey: string | null = null,
  limit = 20,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<DldBuilding[]> {
  const { data, error } = await client.rpc('search_dld_buildings', {
    p_query: query,
    p_community_key: communityKey ?? undefined,
    p_zone_key: zoneKey ?? undefined,
    p_limit: limit,
  });
  if (error) throw error;
  return (data ?? []) as DldBuilding[];
}
